import type {Folder} from '@/api/folders';
import type {Job} from '@/api/jobs';

/**
 Jobs grouped by the folder they are filed directly in, in one pass. Replaces a per-folder-row
 `jobs.filter(...)`, which made each sidebar render O(folders x jobs).
 */
export function groupJobsByFolder<T extends Pick<Job, 'folder_id'>>(jobs: T[]): Map<string, T[]> {
  const byFolder = new Map<string, T[]>();
  for (const job of jobs) {
    if (job.folder_id) {
      const bucket = byFolder.get(job.folder_id);
      if (bucket) {
        bucket.push(job);
      } else {
        byFolder.set(job.folder_id, [job]);
      }
    }
  }

  return byFolder;
}

/**
 Each folder's direct count rolled up into every ancestor, so a folder's figure covers its whole
 subtree - the scope the folder aggregate view and the server's total_job_count use. One walk up the
 parent chain per folder, rather than rebuilding every folder's subtree on every render.
 */
export function rollUpSubtreeCounts(
  folders: Array<Pick<Folder, 'id' | 'parent_folder_id'>>,
  directCounts: ReadonlyMap<string, number>
): Map<string, number> {
  const parentOf = new Map(folders.map((f) => [f.id, f.parent_folder_id]));
  const totals = new Map(folders.map((f) => [f.id, 0]));
  for (const folder of folders) {
    const count = directCounts.get(folder.id) ?? 0;
    if (count === 0) {
      continue;
    }

    // The set doubles as the cycle guard, so bad data cannot hang the render.
    const seen = new Set<string>();
    let current: string | null | undefined = folder.id;
    while (current && totals.has(current) && !seen.has(current)) {
      seen.add(current);
      totals.set(current, (totals.get(current) ?? 0) + count);
      current = parentOf.get(current);
    }
  }

  return totals;
}

export type BadgeScope = {
  /** Any search text or chip narrowing the sidebar's job list. */
  readonly filtersActive: boolean;
  /** Mine-only in effect (it only applies to broad-view users). */
  readonly mineOnly: boolean;
};

/**
 The number a folder's badge shows. Unfiltered, it is the server's subtree count, which is right
 from the first render however few pages of jobs have loaded - the loaded-job count it replaces kept
 climbing as pages arrived. The server has no filtered counts yet, so with a search or chip active it
 falls back to counting the loaded jobs that match.
 */
export function folderBadgeCount(
  folder: Pick<Folder, 'id' | 'total_job_count' | 'total_mine_job_count'>,
  scope: BadgeScope,
  loadedSubtreeCounts: ReadonlyMap<string, number>
): number {
  if (scope.filtersActive) {
    return loadedSubtreeCounts.get(folder.id) ?? 0;
  }

  return scope.mineOnly ? folder.total_mine_job_count : folder.total_job_count;
}
